import { DeploymentEnv, Domain, Locale } from '@zooplus/commons-lang';

import breakpoints from '@styles/breakpoints.scss';

function getNumberFromString(definition: string) {
  return Number.parseInt(definition, 10);
}

export const breakpointsNumeric = {
  breakpointXs: getNumberFromString(breakpoints.breakpointXs),
  breakpointSm: getNumberFromString(breakpoints.breakpointSm),
  breakpointMd: getNumberFromString(breakpoints.breakpointMd),
  breakpointLg: getNumberFromString(breakpoints.breakpointLg),
};

export const ENVS: { [key: string]: DeploymentEnv } = {
  RELEASE: 'release',
  TEST: 'test',
  DEV: 'dev',
  BETA: 'beta',
  LOCAL: 'local',
  PROD: 'prod',
};

export const DEFAULT_LOCALE: Locale = 'de-DE';

export const DEFAULT_DOMAIN: Domain = 'zooplus.de';

export const AREA_TITLE = 'checkout';

export const CHECKOUT_STATE_PROJECT_ID = 'checkout-frontend';

export const DISCOUNT_TYPE = {
  FIRST_ORDER_DISCOUNT: 'FirstOrderDiscount',
  LOYALTY_MANAGEMENT: 'LoyaltyManagement',
};

export const MODAL_PORTAL_ID = 'modal-portal-root';

export const PAYMENT_INCOMPLETE_MODAL_SHOW_TIMEOUT = 3000;

export const CART_MESSAGE_TIMEOUT = 5000;

export const LAZY_LOAD_INTERVAL = 1500;

export const LAZY_LOAD_ATTEMPTS_LEFT = 3;

export const SKELETON_BUTTON_LOADING_TIME = 5000;

export const SCROLL_TO_ERROR_VIEW_TIMEOUT = 150;

export const EMAIL_REGEX = /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~.-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]{2,})*$/;
export const KEYCLOAK_DEV_BASE_URL = 'https://login-dev';

export const KEYCLOAK_PROD_BASE_URL = 'https://login';

export const SILENT_CHECK_ENDPOINT = 'web/sso-checkout/silent-check-sso.htm';

export const EXTERNAL_SCRIPT_LOADER_SCRIPT_DEV = 'https://mkt-tech-dev.omt-services.com';

export const EXTERNAL_SCRIPT_LOADER_SCRIPT_PROD = 'https://mkt-tech.omt-services.com';

export const ARTICLE_TYPE = {
  SAVINGS_PLAN: 'SavingsPlan',
  BONUS: 'Bonus',
  FREE: 'Free',
  STANDARD: 'Standard',
  FREE_SAMPLE: 'FreeSample',
  LOYALTY_MANAGEMENT: 'LoyaltyManagement',
};

export const PAGE_TYPE = {
  WEB_CART: 'web_cart',
  WEB_EMPTY_CART: 'web_empty_cart',
  PREVIEW: 'preview',
  WEB_CART_FST: 'web_cart_fst',
  WEB_CART_POST_FST: 'web_cart_post_fst',
};

export const FEE_TYPE = {
  SHIPPING: 'Shipping',
  LOGISTICS: 'Logistics',
  PAYMENT: 'Payment',
};

export const MESSAGE_PLACEMENT = {
  COUPON: 'COUPON',
  INLINE_COUPON: 'INLINE_COUPON',
  SNACKBAR: 'SNACKBAR',
  PAGE_HEADER: 'PAGE_HEADER',
  PAGE_HEADER_MOBILE: 'PAGE_HEADER_MOBILE',
  CART_TABLE_HEADER: 'CART_TABLE_HEADER',
  CART_SUMMARY_HEADER: 'CART_SUMMARY_HEADER',
  CART_SUMMARY_FOOTER: 'CART_SUMMARY_FOOTER',
  CART_EMPTY_PAGE_HEADER: 'CART_EMPTY_PAGE_HEADER',
  CART_OVERVIEW_BOTTOM_RIGHT: 'CART_OVERVIEW_BOTTOM_RIGHT',
  PREVIEW_CUSTOMER_INFORMATION_HEADER: 'CUSTOMER_INFORMATION_HEADER',
  PREVIEW_CHECK_ORDER_HEADER: 'CHECK_ORDER_HEADER',
  PREVIEW_INLINE_CUSTOMER_INFORMATION: 'INLINE_CUSTOMER_INFORMATION',
  PREVIEW_BOTTOM_RIGHT: 'PREVIEW_BOTTOM_RIGHT',
  SHIPMENT_CONTAINER_HEADER: 'SHIPMENT_CONTAINER_HEADER',
  SHIPMENT_DELIVERY_OPTIONS: 'SHIPMENT_DELIVERY_OPTIONS',
  PLACE_ORDER_BUTTON_POSITION_ABOVE: 'PLACE_ORDER_BUTTON_POSITION_ABOVE',
  PLACE_ORDER_BUTTON_POSITION_ABOVE_MOBILE: 'PLACE_ORDER_BUTTON_POSITION_ABOVE_MOBILE',
};

export const CART_MESSAGE_ACTION_STYLE = {
  LINK: 'LINK',
  BUTTON: 'BUTTON',
};

export const CART_ITEM_IMAGE_DIMENSION = {
  WIDTH: 120,
  HEIGHT: 120,
};

export const PREVIEW_ITEM_IMAGE_DIMENSION = {
  WIDTH: 50,
  HEIGHT: 50,
};

export const PREVIEW_ITEM_IMAGE_DIMENSION_COLLAPSED = {
  WIDTH: 35,
  HEIGHT: 35,
};

export const KEYBOARD_EVENT_CODE = {
  BACKSPACE: 'Backspace',
  DELETE: 'Delete',
};

export const VIEWPORT = {
  MOBILE: 'mobile',
  DESKTOP: 'desktop',
};

export const PIXEL_EXECUTOR_EVENT_TYPE = 'CART_OVERVIEW_PAGE';

export const CUSTOMER_TYPE = {
  NEW_CUSTOMER: 'NC',
  EXISTING_CUSTOMER: 'EC',
  GUEST_CUSTOMER: 'GC',
};

export const PAGE_PATH = {
  CART: '/checkout/cart',
  OVERVIEW: '/checkout/overview',
  PREVIEW: '/checkout/preview',
  GUEST_PREVIEW: '/checkout/guest-preview',
};

export const PAGE_ROUTE = {
  CART: '/cart',
  GUEST_PREVIEW: '/guest-preview',
  PREVIEW: '/preview',
};

export const MESSAGE_CODE = {
  FOD_CAPPING_REACHED: 'LOYALTY_BENEFITS_FIRST_ORDER_DISCOUNT_CAPPING_VALUE_REACHED',
  CHECKOUT_PREVIEW_CUSTOMER_INVALID_EMAIL: 'CHECKOUT_PREVIEW_CUSTOMER_INVALID_EMAIL',
  CHECKOUT_PREVIEW_CUSTOMER_EXISTING_EMAIL: 'CHECKOUT_PREVIEW_CUSTOMER_EXISTING_EMAIL',
  CHECKOUT_ERROR_MINORDER: 'CHECKOUT_ERROR_MINORDER',
  APPLEPAY_NOT_SUPPORTED: 'APPLEPAY_NOT_SUPPORTED',
};

export const CART_MESSAGE_ACTION_TYPE = {
  ADD_COUPON: 'ADD_COUPON',
  REMOVE_COUPON: 'REMOVE_COUPON',
  RESTORE_ARTICLE: 'RESTORE_ARTICLE',
};

export const TRACKING_EVENT_TYPE = {
  CLICK_EVENT: 'Click',
  IMPRESSION_EVENT: 'Impression',
};

export const MINI_AUTOSHIPMENT_BOX = {
  IS_STICKY: 'autoshipment-sticky',
  IS_NOT_STICKY: 'autoshipment-not-sticky',
};

export const CART_VERSION = {
  V1: 'v1',
  V2: 'v2',
};

export const PAYMENT_METHOD = {
  DIRECT_DEBIT_SEPA: 'BS',
  APPLE_PAY: 'AP',
  PAYPAL: 'PP',
  CREDIT_CARD: 'K',
  BRAINTREE: 'BRAINTREE',
  BANCONTACT: 'BC',
};

export const PRICE_TYPE = {
  REDUCED_PRICE: 'REDUCED_PRICE',
  STANDARD_PRICE: 'STANDARD_PRICE',
  BEST_PRICE: 'BEST_PRICE',
};

export const ARTICLE_PRICE_TYPE = {
  SET_ARTICLE: 'set_article',
  SUPPLIER_REDUCED: 'supplier_reduced',
  STANDARD_REDUCED: 'standard_reduced',
  STANDARD: 'standard',
};

export const ARTICLE_PRICE_TYPE_V2 = {
  OFFER: 'OFFER',
  REGULAR: 'REGULAR',
  RETAIL: 'RETAIL',
  SET: 'SET',
};

export const PAGE_LAYOUT = {
  WIDER: 'wider',
  NARROW: 'narrow',
};

export const REALM = {
  ZOOPLUS: 'zooplus',
  BITIBA: 'bitiba',
};

export const RECOMMENDATION_API_TIMEOUT = 10000;

export const RECOMMENDATION_API_VERSION = 'v4';

export const CUSTOMER_STATUS = {
  TNC: 'TNC',
  NOT_TNC: 'NOT_TNC',
  UNDEFINED: 'UNDEFINED',
};

export const REMOVE_YELLOW_BACKGROUND_RRP_EXPERIMENT_ID =
  'idpo-1948-removing_yellow_background_rrp_experiment';

// temporarily placed here, this constant file will be restructured in following ticket: https://tracker.zooplus.de/browse/SC-4606
export const ADOBE_DATA_LAYER = 'adobeDataLayer';
export const ADOBE_TRACKING = {
  EMPTY_CART_PAGE_IDENTIFIER: 'Cart Empty',
  CART_OVERVIEW_PAGE_IDENTIFIER: 'Cart Overview',
  PREVIEW_PAGE_IDENTIFIER: 'Preview',
  CLICK_EVENT_GENERIC: 'Click Event Generic',
  CLICK_EVENT_SPECIFIC: 'Click Event Specific',
  CONTINUE_SHOPPING: 'Continue Shopping',
  CONTINUE_AS_GUEST: 'Continue as guest',
  PAGE_REFRESH: 'Page Refresh',
  CART_ADDITION: 'Cart addition',
  CART_REMOVAL: 'Cart removal',
  ARTICLE_READDITION: 'Article readdition',
  UPDATE_CART_MESSAGES: 'Update Cart Messages',
  OVERWEIGHT_FEE: 'Overweight Fee',
  OVERWEIGHT_FEE_DETAIL: 'overweight:detail',
  ADD_COUPON_TRACKING: 'Checkout: Click in Add Coupon',
  COUPON_REDEEM_CLICK_TRACKING: 'Checkout: Coupon Redeem - Click',
  COUPON_REMOVAL_CLICK_TRACKING: 'Checkout: Coupon Removed',
  COUPON_READDITION_CLICK_TRACKING: 'Checkout: Coupon Readded',
  COUPON_ADDED_TRACKING: 'Checkout: Coupon Added',
  ABO_TOGGLE: 'ABO Toggle',
  ABO_TOGGLE_ON: 'ABO ToggleOn',
  ABO_TOGGLE_OFF: 'ABO ToggleOff',
};

export const SEPA_EXPRESS_LIBRARY_PROD_URL =
  'https://sepaexpress-prod-fx.azurewebsites.net/sepaexpress.js';
export const SEPA_EXPRESS_LIBRARY_DEV_URL =
  'https://sepaexpress-sand-fx.azurewebsites.net/sepaexpress.js';
export const SEPA_EXPRESS_TIMEOUT = 5000;
export const MOBILE_APP_COOKIE = 'MOBILE_APP';

export enum TRACKING_DATA_EVENT {
  PAGE_VIEW = 'PAGE_VIEW',
  VIEW_CART = 'VIEW_CART',
  CLICK = 'CLICK',
  IMPRESSION = 'IMPRESSION',
  EXPERIMENT = 'EXPERIMENT',
}

export enum TRACKING_DATA_PAGE {
  CHECKOUT_CART = 'CHECKOUT_CART',
  CHECKOUT_PREVIEW = 'CHECKOUT_PREVIEW',
}

export enum TARGET_ACTION_ID {
  ADD_COUPON = 'add_coupon',
  ADD_COUPON_SUCCESS = 'successfully_added_coupon',
  REMOVE_COUPON = 'remove_coupon',
  REDEEM_COUPON = 'redeem_coupon',
  CUSTOMER_REGISTRATION = 'customer_registration',
  GUEST_REGISTRATION = 'guest_registration',
  EDIT_BASKET = 'edit_basket',
  NEWSLETTER_CHECKED = 'newsletter_checked',
  PLACE_ORDER_BUTTON = 'place_order_button',
  PLACE_ORDER_BUTTON_DISABLED = 'place_order_button_disabled',
  GOTO_PREVIEW_BUTTON = 'goto_preview_button',
  CONTINUE_TO_CHECKOUT_BUTTON = 'goto_checkout_button',
  CONTINUE_TO_CHECKOUT_BUTTON_DISABLED = 'goto_checkout_button_disabled',
  ACCOUNT_BENEFITS = 'regular_account_benefits_to_guest',
  CREATE_ACCOUNT_FROM_GUEST = 'create_account_from_guest',
  SIGN_IN_FROM_GUEST = 'sign_in_from_guest',
  EDIT_EMAIL_SECTION = 'edit_email_section',
  EDIT_PAYMENT_SECTION = 'edit_payment_section',
}

export enum TARGET_COMPONENT_NAME {
  COUPON = 'Coupons',
  PREVIEW_CONTAINER = 'ContainerComponent',
  NEWSLETTER = 'NewsletterSubscription',
  PLACE_ORDER = 'PreviewPlaceOrderButton',
  CUSTOMER_INFORMATION = 'CustomerInformation',
  CART_SUMMARY = 'Cart_Summary',
}

export enum LINE_ITEM_DISCOUNT {
  ABD = 'ABD',
  P_ABD = 'P_ABD',
  SUBSCRIPTION = 'SUBSCRIPTION',
}

export const PLACE_ORDER_URL = '/checkout/place-order';

export const CART_TYPE = {
  FIRST_PARTY: 'FIRST_PARTY',
  THIRD_PARTY: 'THIRD_PARTY',
  MIXED: 'MIXED',
};

export const BUTTON_STATUS = {
  DISABLED: 'disabled',
};

export const MODERNIZE_PREVIEW = 'new-preview';
